import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Headphones, Plus, Lock, Calendar, MapPin, CheckCircle2, Clock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const statusInfo = (s) => ({
  pendente: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  em_analise: { label: "Em análise", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  respondida: { label: "Respondida", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  agendada: { label: "Agendada", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  concluida: { label: "Concluída", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-rose-100 text-rose-800 border-rose-200" },
})[s] || { label: s, cls: "" };

const THEMES = [
  "Processo Legislativo",
  "Lei Orgânica / Regimento",
  "Fiscalização e Controle",
  "CPI Municipal",
  "Lei 14.133",
  "ETP / TR / Edital",
  "Pregão Eletrônico",
  "Contrato Administrativo",
  "Pesquisa de Preços",
  "Outro",
];

export default function Consultorias() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [open, setOpen] = useState(false);
  const [openPres, setOpenPres] = useState(false);
  const [form, setForm] = useState({ type: "online", theme: THEMES[0], description: "", requested_date: "", attachment_name: "", attachment_data: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/consultancies/me").then(r => setItems(r.data || [])).catch(() => {});

  useEffect(() => {
    load();
    api.get("/achievements/me").then(r => setAchievements(r.data)).catch(() => {});
  }, []);

  const onlineUnlocked = !!achievements?.benefits_unlocked?.consultancy_online;
  const presencialUnlocked = !!achievements?.benefits_unlocked?.consultancy_presencial;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(f => ({ ...f, attachment_name: file.name, attachment_data: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        type: form.type,
        theme: form.theme,
        description: form.description,
      };
      if (form.type === "presencial" && form.requested_date) payload.requested_date = new Date(form.requested_date).toISOString();
      if (form.attachment_data) {
        payload.attachment_name = form.attachment_name;
        payload.attachment_data = form.attachment_data;
      }
      await api.post("/consultancies", payload);
      toast.success("Solicitação enviada! Responderemos em até 48h dentro do app.");
      setOpen(false); setOpenPres(false);
      setForm({ type: "online", theme: THEMES[0], description: "", requested_date: "", attachment_name: "", attachment_data: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setSubmitting(false); }
  };

  const openOnline = () => {
    if (!onlineUnlocked) { toast.error("Consultoria online ainda não desbloqueada. Acesse Conquistas para ver os requisitos."); return; }
    setForm(f => ({ ...f, type: "online" }));
    setOpen(true);
  };
  const openPresencial = () => {
    if (!presencialUnlocked) { toast.error("Consultoria presencial é exclusiva para Especialistas e Embaixadores CEAP."); return; }
    setForm(f => ({ ...f, type: "presencial" }));
    setOpenPres(true);
  };

  return (
    <div className="space-y-4" data-testid="consultorias-page">
      <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Consultorias</h1>
      <p className="text-sm text-muted-foreground">Solicite atendimento técnico do time CEAP. Resposta em até 48h dentro do app.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card className={`rounded-xl ${onlineUnlocked ? "border-[hsl(var(--accent)/0.35)]" : "opacity-90"}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))] flex items-center justify-center">
                <Headphones className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Consultoria Online</h3>
                <p className="text-[11px] text-muted-foreground">Liberada a partir do nível Aluno Recorrente (40h)</p>
              </div>
            </div>
            <Button size="sm" className="mt-3 w-full" onClick={openOnline} data-testid="consultancy-online-btn">
              {onlineUnlocked ? <><Plus className="w-4 h-4 mr-1" /> Solicitar Consultoria Online</> : <><Lock className="w-4 h-4 mr-1" /> Bloqueado</>}
            </Button>
            {!onlineUnlocked && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Você está a {Math.max(0, (achievements?.settings?.hours_recorrente || 40) - (achievements?.hours_accumulated || 0)).toFixed(0)}h de desbloquear. <Link className="text-[hsl(var(--accent))]" to="/conquistas">Ver Conquistas</Link>
              </p>
            )}
          </CardContent>
        </Card>

        <Card className={`rounded-xl ${presencialUnlocked ? "border-[hsl(var(--accent)/0.35)]" : "opacity-90"}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))] flex items-center justify-center">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">Consultoria Presencial</h3>
                <p className="text-[11px] text-muted-foreground">Sede CEAP em Brasília • Especialistas e Embaixadores</p>
              </div>
            </div>
            <Button size="sm" className="mt-3 w-full" variant={presencialUnlocked ? "default" : "outline"} onClick={openPresencial} data-testid="consultancy-presential-btn">
              {presencialUnlocked ? <><Calendar className="w-4 h-4 mr-1" /> Agendar Consultoria Presencial</> : <><Lock className="w-4 h-4 mr-1" /> Bloqueado</>}
            </Button>
          </CardContent>
        </Card>
      </div>

      <h2 className="font-display font-semibold text-base text-[hsl(var(--primary))] mt-2">Minhas solicitações</h2>
      {items.length === 0 ? <EmptyState icon={Headphones} title="Nenhuma solicitação ainda" description="Quando solicitar uma consultoria, ela aparecerá aqui." />
        : items.map(c => {
          const s = statusInfo(c.status);
          return (
            <Card key={c.id} className="rounded-xl" data-testid={`consultancy-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Badge variant="secondary" className="capitalize">{c.type === "online" ? "Online" : "Presencial"}</Badge>
                  <Badge className={s.cls} variant="outline">{s.label}</Badge>
                </div>
                <h3 className="font-display font-semibold text-[hsl(var(--primary))]">{c.theme}</h3>
                <p className="text-sm text-muted-foreground mt-1">{c.description}</p>
                {c.requested_date && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Data desejada: {new Date(c.requested_date).toLocaleDateString("pt-BR")}</p>
                )}
                {c.scheduled_date && (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Agendada para: {new Date(c.scheduled_date).toLocaleDateString("pt-BR")}</p>
                )}
                {c.response && (
                  <div className="mt-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-3">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Resposta CEAP</p>
                    <p className="text-sm whitespace-pre-wrap">{c.response}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

      <Dialog open={open || openPres} onOpenChange={(o) => { if (!o) { setOpen(false); setOpenPres(false); } }}>
        <DialogContent className="max-w-[480px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{openPres ? "Consultoria Presencial" : "Consultoria Online"}</DialogTitle>
            <DialogDescription>Receberemos sua dúvida e responderemos em até 48h dentro do aplicativo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Tema</Label>
              <Select value={form.theme} onValueChange={(v) => setForm(f => ({ ...f, theme: v }))}>
                <SelectTrigger data-testid="consultancy-theme"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THEMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Dúvida / contexto</Label><Textarea required rows={5} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descreva sua dúvida em detalhes…" data-testid="consultancy-description" /></div>
            {openPres && (
              <div><Label>Data desejada</Label><Input type="date" value={form.requested_date} onChange={(e) => setForm(f => ({ ...f, requested_date: e.target.value }))} data-testid="consultancy-date" /></div>
            )}
            <div><Label>Anexar arquivo (opcional, máx 5MB)</Label>
              <Input type="file" onChange={handleFile} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" />
              {form.attachment_name && <p className="text-[11px] text-muted-foreground mt-1">{form.attachment_name}</p>}
            </div>
            <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="consultancy-submit">
              {submitting ? "Enviando…" : "Enviar solicitação"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
